/**
 * Global auth/network interceptor for API-base requests.
 *
 * BUSINESS LOGIC:
 * - attach the live access token to backend requests
 * - recover from 401 responses through one shared refresh flow
 * - redirect 403 responses back to the dashboard after surfacing a global error
 * - avoid duplicate global error toasts for repeated failures
 */
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { TokenService } from '../services/token.service';
import { environment } from '../../../environments/environment';
import { UiToastService } from '../../shared/ui/ui-toast/ui-toast.service';
import { getApiErrorMessage } from '../../shared/utils';
import { AuthService } from '../../features/login/auth.service';

// Module-level state shared across all interceptor invocations.
// Prevents multiple simultaneous refresh calls when concurrent requests all get 401.
let isRefreshing = false;
const newAccessToken$ = new BehaviorSubject<string | null>(null);
let lastToastKey = '';
let lastToastAt = 0;

interface RefreshApiResponse {
  data?: { accessToken?: string; refreshToken?: string };
  accessToken?: string;
  refreshToken?: string;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const router = inject(Router);
  const http = inject(HttpClient);
  const toast = inject(UiToastService);
  const authService = inject(AuthService);
  const isApiRequest = req.url.startsWith(environment.apiBaseUrl);

  if (!isApiRequest) {
    return next(req);
  }

  const token = tokenService.getAccessToken();
  const outgoingReq = token ? attachToken(req, token) : req;

  return next(outgoingReq).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 403) {
        showGlobalHttpError(toast, err);

        if (!req.url.includes('/auth/me')) {
          void authService.loadProfile().catch(() => undefined);
        }

        redirectToDashboard(router);
        return throwError(() => err);
      }

      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        showGlobalHttpError(toast, err);
        return throwError(() => err);
      }

      // Preferences endpoint is best-effort — don't kill the session on failure.
      if (req.url.includes('/auth/me/preferences')) {
        return throwError(() => err);
      }

      // Refresh endpoint itself returned 401 — no recovery possible.
      if (req.url.includes('/auth/refresh')) {
        showGlobalHttpError(toast, err);
        clearAndRedirect(tokenService, router);
        return throwError(() => err);
      }

      const refreshToken = tokenService.getRefreshToken();

      if (!refreshToken) {
        showGlobalHttpError(toast, err);
        clearAndRedirect(tokenService, router);
        return throwError(() => err);
      }

      if (isRefreshing) {
        // Another request is already refreshing — wait for the new token then retry.
        return newAccessToken$.pipe(
          filter((t): t is string => t !== null),
          take(1),
          switchMap(newToken => next(attachToken(req, newToken)).pipe(
            catchError((retryErr: unknown) => {
              showGlobalHttpError(toast, retryErr);
              return throwError(() => retryErr);
            })
          ))
        );
      }

      isRefreshing = true;
      newAccessToken$.next(null);

      return http
        .post<RefreshApiResponse>(`${environment.apiBaseUrl}/auth/refresh`, { refreshToken })
        .pipe(
          switchMap(res => {
            const accessToken = res?.data?.accessToken ?? res?.accessToken ?? null;
            const updatedRefreshToken = res?.data?.refreshToken ?? res?.refreshToken ?? null;

            if (!accessToken) {
              isRefreshing = false;
              clearAndRedirect(tokenService, router);
              const tokenError = new Error('Refresh response missing access token');
              showGlobalHttpError(toast, tokenError);
              return throwError(() => tokenError);
            }

            tokenService.setAccessToken(accessToken);
            if (updatedRefreshToken) {
              localStorage.setItem('refreshToken', updatedRefreshToken);
            }

            isRefreshing = false;
            newAccessToken$.next(accessToken);

            return next(attachToken(req, accessToken)).pipe(
              catchError((retryErr: unknown) => {
                showGlobalHttpError(toast, retryErr);
                return throwError(() => retryErr);
              })
            );
          }),
          catchError(refreshErr => {
            isRefreshing = false;
            showGlobalHttpError(toast, refreshErr);
            clearAndRedirect(tokenService, router);
            return throwError(() => refreshErr);
          })
        );
    })
  );
};

function attachToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * Clears local session state when recovery is no longer possible and sends the user to login.
 */
function clearAndRedirect(tokenService: TokenService, router: Router): void {
  tokenService.clearTokens();
  void router.navigateByUrl('/login');
}

function redirectToDashboard(router: Router): void {
  void router.navigateByUrl('/dashboard');
}

/**
 * Deduplicates global HTTP error toasts so a burst of failing requests does not spam the UI.
 */
function showGlobalHttpError(toast: UiToastService, err: unknown): void {
  const message = getApiErrorMessage(err, 'Something went wrong. Please try again.');
  const key = `${message}|${err instanceof HttpErrorResponse ? err.status : 'unknown'}`;
  const now = Date.now();

  if (key === lastToastKey && now - lastToastAt < 3000) {
    return;
  }

  lastToastKey = key;
  lastToastAt = now;
  toast.error('Error', message, 5000);
}
